<script setup lang="ts">
import { computed, ref } from "vue";
import { EditPencil, MoreVert, Plus, Trash } from "@iconoir/vue";
import draggable from "vuedraggable";
import { deburr } from "lodash-es";
import type { AppPage } from "@artech-global/types";

// Same shape as the client's form field builder: a compact draggable list, one
// row per page, edited in place instead of a wall of inputs.
const props = withDefaults(
  defineProps<{
    modelValue: AppPage[];
    errors?: Record<string, string>;
    max?: number;
  }>(),
  { errors: () => ({}), max: 8 },
);

const emit = defineEmits<{
  (e: "update:modelValue", value: AppPage[]): void;
}>();

const { t } = useI18n();

const editingIndex = ref<number | null>(null);
const draft = ref<AppPage | null>(null);
const isNew = ref(false);

const pages = computed({
  get: () => props.modelValue ?? [],
  set: (value: AppPage[]) => emit("update:modelValue", value),
});

// Page keys end up in workspace URLs (/apps/:slug/:key).
function toKebab(value: string) {
  return deburr(value)
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-|-$/g, "");
}

function rowError(index: number) {
  return ["label", "key", "path"].map((f) => props.errors[`pages[${index}].${f}`]).find(Boolean);
}

function openEditor(index: number) {
  editingIndex.value = index;
  draft.value = { ...pages.value[index]! };
  isNew.value = false;
}

function closeEditor(cancelled = false) {
  if (cancelled && isNew.value && editingIndex.value !== null) remove(editingIndex.value);
  editingIndex.value = null;
  draft.value = null;
  isNew.value = false;
}

function saveEditor() {
  if (editingIndex.value === null || !draft.value) return;
  const label = draft.value.label.trim();
  const next = [...pages.value];
  // An empty key follows the label, the way the slug follows the name.
  next[editingIndex.value] = {
    label,
    key: toKebab(draft.value.key || label),
    path: draft.value.path.trim() || "/",
  };
  emit("update:modelValue", next);
  closeEditor();
}

function remove(index: number) {
  emit("update:modelValue", pages.value.filter((_, i) => i !== index));
  if (editingIndex.value === index) closeEditor();
}

function add() {
  if (pages.value.length >= props.max) return;
  emit("update:modelValue", [...pages.value, { key: "", label: "", path: "/" }]);
  editingIndex.value = pages.value.length;
  draft.value = { key: "", label: "", path: "/" };
  isNew.value = true;
}
</script>

<template>
  <div class="page-builder flex flex-col gap-small">
    <draggable
      v-if="pages.length"
      v-model="pages"
      :item-key="(el: AppPage) => pages.indexOf(el)"
      handle=".page-builder__handle"
      class="flex flex-col gap-small"
      :animation="150"
      :disabled="editingIndex !== null"
      ghost-class="opacity-50"
    >
      <template #item="{ index }">
        <div class="page-builder__item" :class="{ 'is-editing': editingIndex === index }">
          <!-- COLLAPSED -->
          <div v-if="editingIndex !== index" class="page-builder__row">
            <span class="page-builder__handle" aria-hidden="true">
              <MoreVert class="icon" />
            </span>
            <div class="flex flex-col flex-1 min-w-0 gap-3xsmall">
              <span class="text-body-secondary content-neutral-strong page-builder__text">
                {{ pages[index]?.label || t("app.pageUntitled") }}
              </span>
              <span v-if="rowError(index)" class="caption-tertiary content-negative-medium page-builder__text">
                {{ rowError(index) }}
              </span>
              <span v-else class="caption-tertiary content-neutral-light page-builder__text">
                {{ pages[index]?.key || "—" }} · {{ pages[index]?.path }}
              </span>
            </div>
            <div class="inline-flex items-center gap-2xsmall flex-none">
              <ButtonIcon
                variant="neutral-flat"
                size="small"
                :aria-label="t('action.edit')"
                @click="openEditor(index)"
              >
                <EditPencil class="icon" />
              </ButtonIcon>
              <ButtonIcon
                variant="neutral-flat"
                size="small"
                :aria-label="t('action.delete')"
                @click="remove(index)"
              >
                <Trash class="icon" />
              </ButtonIcon>
            </div>
          </div>

          <!-- EDITOR -->
          <div v-else-if="draft" class="page-builder__editor">
            <div class="page-builder__fields">
              <FormField
                v-model="draft.label"
                size="sm"
                :label="t('app.field.pageLabel')"
                :error="errors[`pages[${index}].label`]"
                @keydown.enter.prevent="saveEditor"
              />
              <FormField
                v-model="draft.key"
                size="sm"
                :label="t('app.field.pageKey')"
                :help-text="t('app.pageKeyNote')"
                :error="errors[`pages[${index}].key`]"
                @keydown.enter.prevent="saveEditor"
              />
              <FormField
                v-model="draft.path"
                size="sm"
                :label="t('app.field.pagePath')"
                :error="errors[`pages[${index}].path`]"
                @keydown.enter.prevent="saveEditor"
              />
            </div>

            <div class="flex items-center justify-between gap-small">
              <Button v-if="!isNew" variant="danger-secondary" size="small" @click="remove(index)">
                <Trash class="icon" />
                {{ t("action.delete") }}
              </Button>
              <div class="flex items-center gap-small ml-auto">
                <Button variant="tertiary" size="small" @click="closeEditor(true)">
                  {{ t("action.cancel") }}
                </Button>
                <Button variant="primary" size="small" :disabled="!draft.label.trim()" @click="saveEditor">
                  {{ t("action.save") }}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </draggable>

    <Button
      v-if="editingIndex === null"
      variant="tertiary"
      size="small"
      class="page-builder__add w-fit"
      :disabled="pages.length >= max"
      @click="add"
    >
      <Plus class="icon" />
      {{ t("app.addPage") }}
    </Button>
  </div>
</template>

<style scoped lang="scss">
.page-builder {
  &__item {
    border: 1px solid token("color-border-neutral-light-default");
    border-radius: token("corner-radius-small");
    overflow: hidden;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: token("gap-small");
    min-height: 52px;
    padding: 0 token("padding-small");
  }

  &__handle {
    display: inline-flex;
    align-items: center;
    flex: none;
    color: token("color-content-neutral-strong");
    cursor: grab;

    &:active {
      cursor: grabbing;
    }

    .icon {
      width: token("icon-size-small");
      height: token("icon-size-small");
    }
  }

  &__text {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__editor {
    display: flex;
    flex-direction: column;
    gap: token("gap-medium");
    padding: token("padding-medium");
  }

  &__fields {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    align-items: start;
    gap: token("gap-small");

    @include down("md") {
      grid-template-columns: 1fr;
    }
  }

  &__add {
    border-style: dashed;
  }
}
</style>
